import type { ChangeEvent, FC } from "react";
import { useEffect, useState } from "react";
import { Autocomplete, ListItem, styled, TextField } from "@mui/material";
import type { UiSchema, WidgetProps, RJSFSchema } from "@rjsf/utils";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import i18next from "i18next";
import { getValueFromUiSchema } from "@/utils/schema";
import Api from "@/services/Api";
import type { KioFormContext } from "@/framework/KioForm/KioForm";
import type { RequestContext } from "@/declarations/RequestContext";
import { getLanguageCode, resolvePotentiallyLocalizedString } from "@/utils/obj";
import { sortString } from "@/utils/sort";
import type { ApplicationInstance } from "@/declarations/models/ApplicationInstance";

const StyledListItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== "aria-selected" })(
  ({ "aria-selected": selected, theme }) => `
    padding-left: ${selected ? "12px !important" : ""};
    border-left: ${selected ? `${theme.palette.primary.main} solid 4px` : ""};
  `
);

function getTitle(schema: RJSFSchema, uiSchema: UiSchema): string {
  return schema.title ?? getValueFromUiSchema("title", uiSchema) ?? "";
}

interface EntityAutocompleteProps extends WidgetProps {
  limitToMuseumsApiId?: number;
}

const EntityAutocompleteWidget: FC<EntityAutocompleteProps> = ({
  options,
  onChange,
  value,
  required,
  disabled,
  formContext,
  uiSchema,
  ...rest
}) => {
  const { variant }: KioFormContext = formContext;
  const { t } = useTranslation("common");
  const [selectOptions, setSelectOptions] = useState<any[]>([]);
  const [autocompleteValue, setAutocompleteValue] = useState<object | null>(null);
  const [configError, setConfigError] = useState<string | undefined>(undefined);
  const [inputValue, setInputValue] = useState<string>("");
  const getLocalizedString = resolvePotentiallyLocalizedString(getLanguageCode(i18next.language));

  const displayFieldName = String(options["displayField"]);
  const valueFieldName = String(options["valueField"]);
  const getOptionLabel = (item: { [key: string]: any }) =>
    getLocalizedString(item[displayFieldName]) ?? item[displayFieldName];
  const title = uiSchema ? getTitle(rest.schema, uiSchema) : "";
  const { id } = useParams<{ id?: string }>();

  useEffect(() => {
    // convert input value to one of the available options
    setAutocompleteValue(selectOptions.find((o) => o[valueFieldName]?.toString() === value?.toString()));
  }, [value, selectOptions, valueFieldName]);

  useEffect(() => {
    // set selectOptions based on ui:options type
    let ctx: RequestContext<any>;
    switch (options.type) {
      case "TranslationKey":
        ctx = Api.getAllTranslationKeys({ sort_by: "translation_key" });
        break;
      case "Owner":
        ctx = Api.getAllOwners({ sort_by: "name" });
        break;
      case "Application":
        ctx = Api.getAllApplications({ sort_by: "name" });
        break;
      case "Locale":
        ctx = Api.getAllLocales({ sort_by: "name" });
        break;
      case "CreditType":
        ctx = Api.getCreditTypes();
        break;
      case "Schema":
        ctx = Api.getAllSchemas(id ? { sort_by: "name", application_id: +id } : { sort_by: "name" });
        break;
      case "ApplicationInstance":
        ctx = Api.getAllInstances();
        break;
      case "IdentifierType":
        ctx = Api.getAllIdentifierTypes({ page_size: 100 });
        break;
      case "PCSClient":
        if (!rest.limitToMuseumsApiId) {
          ctx = Api.getAllPCSRemoteClients();
          break;
        }
        ctx = Api.getAllPCSRemoteClients({ m_ids: [rest.limitToMuseumsApiId] });
        break;
      default:
        setConfigError(`Unsupported "type"`);
        return;
    }
    ctx
      .fetchDirect({ items: [] })
      .then(async (result) => result.items)
      .then((selOps: any[]) => {
        if (options.type === "ApplicationInstance") {
          if (id) {
            const currentInstance = selOps.find((instance: ApplicationInstance) => instance.id === +id);
            setSelectOptions(
              selOps
                .filter(
                  (instance: ApplicationInstance) =>
                    instance.id !== currentInstance.id && instance.application_id === currentInstance?.application_id
                )
                .map((instance: ApplicationInstance) => ({
                  id: instance.id,
                  name: `${getLocalizedString(instance.owner_name)} (${instance.application_name})`,
                }))
                .sort((el1, el2) => sortString(el1.name, el2.name))
            );
          } else {
            setSelectOptions(
              selOps
                .map((instance: ApplicationInstance) => ({
                  id: instance.id,
                  name: `${getLocalizedString(instance.owner_name)} (${instance.application_name})`,
                }))
                .sort((el1, el2) => sortString(el1.name, el2.name))
            );
          }
        } else if (options.type == "IdentifierType") {
          setSelectOptions(
            selOps
              .map((opt: any) => ({
                id: opt.id,
                identifier_type: opt.identifier_type,
              }))
              .sort((el1, el2) => sortString(el1.identifier_type, el2.identifier_type))
          );
        } else if (options.type == "PCSClient") {
          if (selOps.length === 0) {
            setConfigError("No clients found. Or there was an error connecting to PCS.");
          } else {
            setConfigError(undefined);
            setSelectOptions(
              selOps
                .map((opt: any) => ({
                  id: opt.id,
                  name: `${opt.ownerId} - ${opt.groupName ?? "Ungrouped"} - ${opt.name} (ID: ${opt.id})`,
                }))
                .sort((el1, el2) => sortString(el1.name, el2.name))
            );
          }
        } else if (options.type !== "CreditType" && options.type !== "Owner") setSelectOptions(selOps);
        else if (options.type == "Owner")
          setSelectOptions(
            selOps.map((opt: any) => ({
              id: opt.id,
              name: getLocalizedString(opt.name),
            }))
          );
        else {
          const _selopsTemp = selOps.map((opt: any) => ({
            id: opt.id,
            name: t(`editMedia.creditTypes.${opt.name}`),
          }));
          _selopsTemp.sort((el1, el2) => sortString(el1.name, el2.name));
          setSelectOptions(_selopsTemp);
        }
      });
    return () => ctx.abort();
  }, [options]);

  const onChangeHandler = (e: ChangeEvent<{}>, newValue: { [key: string]: any } | null) => {
    setAutocompleteValue(newValue || null);
    onChange && onChange(newValue ? newValue[valueFieldName]?.toString() : undefined);
  };

  return (
    <Autocomplete
      aria-required={required}
      disabled={disabled || !!configError}
      getOptionLabel={getOptionLabel}
      options={selectOptions}
      value={autocompleteValue || null}
      defaultValue={null}
      inputValue={inputValue}
      onInputChange={(_, newValue) => setInputValue(newValue)}
      onChange={onChangeHandler}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          disabled={disabled || !!configError}
          required={required}
          label={title}
          // InputProps={{
          //   endAdornment: <Loader size={18} />,
          // }}
          helperText={configError || getValueFromUiSchema("help", uiSchema)}
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <StyledListItem key={key} {...rest}>
            {getOptionLabel(option)}
          </StyledListItem>
        );
      }}
      fullWidth
      isOptionEqualToValue={(option, value) => value.id === option.id}
    />
  );
};

export default EntityAutocompleteWidget;
